/**
 * resolveDrilledSubqueryBodyEnd — re-derive the CURRENT end offset of a drilled
 * subquery body from its (stable) start offset, after the buffer changed under
 * an inner edit.
 *
 * The drill entry stores absolute `bodyStart`/`bodyEnd`. Every inner edit lands
 * at/after `bodyStart`, so `bodyStart` stays valid — but `bodyEnd` shifts by the
 * edit's length delta and goes stale. We re-scan from `bodyStart` at paren-depth
 * 1 (the body sits just inside the subquery's own opening paren) to the matching
 * close paren, token-aware (strings / comments / quoted identifiers don't count),
 * then trim trailing whitespace.
 *
 * Returns the trimmed end offset, or `null` when the parens are unbalanced OR the
 * body no longer classifies as a SELECT/WITH/VALUES subquery — both mean the
 * drill is stale and the caller should clear it. Pure / structural (no regex
 * parse of SQL beyond the shared token-skip primitive).
 */
import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { detectSubqueryBodyKind } from './extractSubqueries';

export function resolveDrilledSubqueryBodyEnd(
  source: string,
  bodyStart: number,
  options: ParseOptions,
): number | null {
  const ctx = skipContextFor(options);
  let depth = 1;
  let i = bodyStart;
  while (i < source.length) {
    const skipped = trySkipNonCode(source, i, source.length, ctx);
    if (skipped >= 0) {
      i = skipped;
      continue;
    }
    const c = source[i];
    if (c === '(') {
      depth++;
      i++;
      continue;
    }
    if (c === ')') {
      depth--;
      if (depth === 0) {
        let end = i;
        while (end > bodyStart && /\s/.test(source[end - 1])) end--;
        if (end <= bodyStart) return null;
        if (detectSubqueryBodyKind(source, bodyStart, end) === 'unknown') return null;
        return end;
      }
      i++;
      continue;
    }
    i++;
  }
  return null;
}
